import React, { useEffect, useMemo } from 'react';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import ListingGrid from '../../listing_grid';
import { ListingItem } from '../interspersed_listings';
import { SearchRemixer_MarketplaceListingsSearch } from './query_helpers';
import {
  CombinedMarketplaceBumpTrackingMutation,
  CombinedMarketplaceBumpTrackingMutationVariables,
  PromotionType,
} from '@reverbdotcom/commons/src/gql/graphql';

const COMPONENT_NAME = 'CombinedMarketplaceGrid';

interface CombinedBumpListingGridProps {
  listings: SearchRemixer_MarketplaceListingsSearch['listings'];
  promotionInfo: SearchRemixer_MarketplaceListingsSearch['promotionInfo'];
  loading: boolean;
  error?: boolean;
  isListView: boolean;
}

const BUMP_TRACKING_MUTATION = gql(`
  mutation CombinedMarketplaceBumpTracking($input: Input_arbiter_BulkTrackImpressionsRequest) {
    bulkTrackImpressions(input: $input) {
      listingIds
    }
  }
`);

function CombinedBumpListingGrid({
  listings,
  promotionInfo,
  loading,
  error,
  isListView,
}: CombinedBumpListingGridProps) {
  const user = useUser();
  const [trackBumpImpressions] = useMutation<
    CombinedMarketplaceBumpTrackingMutation,
    CombinedMarketplaceBumpTrackingMutationVariables
  >(BUMP_TRACKING_MUTATION);

  const { bumpedListingIds, normalizedListings } = useMemo(() => {
    return getBumpedIdsAndNormalizedListings(listings, promotionInfo);
  }, [listings, promotionInfo]);

  useEffect(() => {
    if (loading || error || bumpedListingIds.length === 0) return;

    trackBumpImpressions({
      variables: {
        input: {
          listingIds: bumpedListingIds,
        },
      },
    });

    trackEvent({
      eventName: MParticleEventName.ListingImpression,
      listingIds: bumpedListingIds,
      listings: listings,
      componentName: COMPONENT_NAME,
    });
  }, [loading, error, bumpedListingIds, trackBumpImpressions, listings]);

  const gridItems = normalizedListings?.map((listing, index) => {
    function listingKey(listing): string {
      if (listing.bumpKey?.key) {
        return `${listing.id}-${listing.bumpKey?.key}`;
      }

      return listing.id;
    }
    return (
      <ListingItem
        key={listingKey(listing)}
        listing={listing}
        idx={index}
        user={user}
        displayAsRows={isListView}
        loading={loading}
      />
    );
  });

  return (
    <ListingGrid
      filters={[]} // this is only used for the sidebar which is rendered on mobile.
      // we should be able to find a solution for web & mobile outside the listings grid later
      loading={loading}
      listings={normalizedListings}
      error={error}
      isListView={isListView}
      gridItems={gridItems}
    />
  );
}

export default CombinedBumpListingGrid;

export function getBumpedIdsAndNormalizedListings(
  listings: SearchRemixer_MarketplaceListingsSearch['listings'],
  promotionInfo: SearchRemixer_MarketplaceListingsSearch['promotionInfo'],
) {
  const bumpedListingIds = (!listings?.length || !promotionInfo?.length) ? [] : listings
    .filter((_, index) => promotionInfo[index]?.promotionType === PromotionType.BUMP)
    .map((listing) => listing.id);

  // We have to fetch every listing's bump key in our initial query,
  // but we only want to keep it if search-remixer marks the listing
  // as promoted via promotionInfo.
  const normalizedListings = listings?.map((listing) => {
    const isBumped = bumpedListingIds.includes(listing.id);
    return {
      ...listing,
      bumpKey: isBumped ? listing.bumpKey : { ...listing.bumpKey, key: '' },
      bumped: isBumped,
    };
  });

  return { bumpedListingIds, normalizedListings };
}
